// ── Marketplace Eligibility Resolver (P0) ──
// Derived, never stored. The single source of truth for whether a contractor
// can receive marketplace offers (P1) and surface as a Smart Dispatch
// recommendation (P0).
//
// Eligibility = ALL blocking factors pass, OR an admin override is "allow".
// An admin override of "deny" hard-fails regardless of factors.
//
// Soft factors (e.g. scorecard rating, capacity) NEVER block — they surface
// as warnings on the offer picker.
//
// Pricing is intentionally absent. This module respects the financial
// visibility policy and never reads contractValue / payoutAmount.

import type { Contractor, Job } from "@/data/types";
import type { ContractorPayProfile } from "@/data/contractorPayProfile";
import { getPayProfile, canAssignContractor } from "@/data/contractorPayProfile";
import { projectTypeToArchetype } from "@/data/payProfileGuard";
import {
  getComplianceStatus,
  type ComplianceRequirementKey,
  type RequirementResolution,
} from "@/data/contractor/compliance/complianceStatus";
import type { ComplianceDocument } from "@/data/contractor/compliance/complianceDocuments";
import { hasRequiredAgreementSignature } from "@/data/contractor/onboarding/onboardingAgreements";
import type { OnboardingSession } from "@/data/contractor/onboarding/types";

export type EligibilityFactorKey =
  | "profile"
  | "pay_profile"
  | "compliance"
  | "coverage"
  | "scorecard"
  | "opt_in"
  | "override";

export interface EligibilityFactor {
  key: EligibilityFactorKey;
  label: string;
  passed: boolean;
  /** Hard-fail if blocking and !passed. Soft factors only inform. */
  blocking: boolean;
  reason: string;
}

export interface ContractorEligibility {
  contractorId: string;
  /** True if all blocking factors pass, modulated by admin override. */
  eligible: boolean;
  /** True when an admin override flipped the result (allow or deny). */
  overrideActive: boolean;
  factors: EligibilityFactor[];
  computedAt: string;
}

export interface EligibilityContext {
  payProfiles: ContractorPayProfile[];
  /** Optional — when provided, coverage + pay-profile-archetype are evaluated against this job. */
  job?: Job;
  /** DB-backed compliance ledger. When present for this contractor, it supersedes legacy booleans. */
  complianceDocuments?: ComplianceDocument[];
  /** Onboarding sessions provide agreement-signature and payout handoff evidence. */
  onboardingSessions?: OnboardingSession[];
  /** Parent contractor for inherited company-level requirements. */
  parentContractor?: Pick<Contractor, "id" | "entityType">;
}

const today = () => new Date();
const isoToday = () => today().toISOString().slice(0, 10);

function isInsuranceCurrent(expiry?: string): boolean {
  if (!expiry) return false;
  return expiry >= isoToday();
}

function evaluateProfile(c: Contractor): EligibilityFactor {
  const passed = c.active && c.onboardingStatus === "Approved";
  return {
    key: "profile",
    label: "Active & approved",
    passed,
    blocking: true,
    reason: passed
      ? "Active, approved profile."
      : !c.active
        ? "Contractor profile is inactive."
        : `Onboarding status is ${c.onboardingStatus}, not Approved.`,
  };
}

function evaluatePayProfile(
  c: Contractor,
  ctx: EligibilityContext,
): EligibilityFactor {
  const archetype = ctx.job ? projectTypeToArchetype(ctx.job.projectType) : undefined;
  if (ctx.job) {
    const check = canAssignContractor(ctx.payProfiles, c.id, archetype);
    return {
      key: "pay_profile",
      label: "Eligibility Profile",
      passed: check.allowed,
      blocking: true,
      reason: check.reason,
    };
  }
  const profile = getPayProfile(ctx.payProfiles, c.id);
  return {
    key: "pay_profile",
    label: "Eligibility Profile",
    passed: !!profile,
    blocking: true,
    reason: profile ? "Active Eligibility Profile present." : "No active Eligibility Profile.",
  };
}

const COMPLIANCE_MARKETPLACE_LABELS: Record<ComplianceRequirementKey, string> = {
  license: "license",
  insurance: "insurance",
  w9: "W-9",
  background_check: "background check",
  agreement: "agreement",
  banking_verification: "banking verification",
  certification: "certification",
};

const MARKETPLACE_COMPLIANCE_EXCLUSIONS = new Set<ComplianceRequirementKey>([
  // Background checks are optional review evidence and should not block offers.
  "background_check",
  // Payout readiness is reviewed during onboarding/compliance handoff,
  // but marketplace eligibility has a separate payout setup flow.
  "banking_verification",
]);

function latestOnboardingSessionFor(
  sessions: OnboardingSession[] | undefined,
  contractorId: string,
): OnboardingSession | undefined {
  return [...(sessions ?? [])]
    .filter((session) => session.contractorId === contractorId)
    .sort(
      (a, b) =>
        Date.parse(b.lastTouchedAt ?? b.startedAt) -
        Date.parse(a.lastTouchedAt ?? a.startedAt),
    )[0];
}

function hasDbBackedComplianceEvidence(
  c: Contractor,
  ctx: EligibilityContext,
): boolean {
  const sourceIds = new Set([c.id, ctx.parentContractor?.id].filter(Boolean));
  const hasDocumentEvidence = (ctx.complianceDocuments ?? []).some((doc) =>
    sourceIds.has(doc.contractorId),
  );
  const session = latestOnboardingSessionFor(ctx.onboardingSessions, c.id);
  return hasDocumentEvidence || !!session;
}

function isRequirementSatisfied(
  resolution: RequirementResolution,
  latestSession: OnboardingSession | undefined,
): boolean {
  if (!resolution.required) return true;
  if (resolution.status === "ok" || resolution.status === "not_required") return true;
  if (resolution.requirement === "agreement" && latestSession) {
    return hasRequiredAgreementSignature(latestSession);
  }
  return false;
}

function formatComplianceReason(
  unresolved: RequirementResolution[],
  latestSession: OnboardingSession | undefined,
): string {
  if (!unresolved.length) {
    return "Required compliance documents and agreement are verified.";
  }
  const pending = unresolved.filter((item) => item.status === "pending_verification");
  const missing = unresolved.filter(
    (item) =>
      item.status === "missing" ||
      item.status === "rejected" ||
      item.status === "expired" ||
      item.status === "expiring_soon" ||
      (item.requirement === "agreement" &&
        !latestSession &&
        item.status !== "pending_verification"),
  );

  const parts: string[] = [];
  if (pending.length) {
    parts.push(
      `Pending verification: ${pending
        .map((item) => COMPLIANCE_MARKETPLACE_LABELS[item.requirement])
        .join(", ")}`,
    );
  }
  if (missing.length) {
    parts.push(
      `Missing or unresolved: ${missing
        .map((item) => COMPLIANCE_MARKETPLACE_LABELS[item.requirement])
        .join(", ")}`,
    );
  }
  return `${parts.join("; ")}.`;
}

function evaluateDbBackedCompliance(
  c: Contractor,
  ctx: EligibilityContext,
): EligibilityFactor {
  const latestSession = latestOnboardingSessionFor(ctx.onboardingSessions, c.id);
  const resolutions = getComplianceStatus(
    c,
    ctx.complianceDocuments ?? [],
    ctx.parentContractor,
  ).requirements.filter(
    (item) => item.required && !MARKETPLACE_COMPLIANCE_EXCLUSIONS.has(item.requirement),
  );
  const unresolved = resolutions.filter(
    (item) => !isRequirementSatisfied(item, latestSession),
  );
  const passed = unresolved.length === 0;
  return {
    key: "compliance",
    label: "Compliance",
    passed,
    blocking: true,
    reason: formatComplianceReason(unresolved, latestSession),
  };
}

function evaluateLegacyCompliance(c: Contractor): EligibilityFactor {
  const insOk = c.insuranceOnFile && isInsuranceCurrent(c.insuranceExpiry);
  const passed = insOk && c.w9OnFile && c.agreementSigned;
  let reason = "Insurance, W-9, and agreement on file.";
  if (!passed) {
    const missing: string[] = [];
    if (!c.insuranceOnFile) missing.push("insurance");
    else if (!isInsuranceCurrent(c.insuranceExpiry)) missing.push("insurance expired");
    if (!c.w9OnFile) missing.push("W-9");
    if (!c.agreementSigned) missing.push("agreement");
    reason = `Missing or expired: ${missing.join(", ")}.`;
  }
  return { key: "compliance", label: "Compliance", passed, blocking: true, reason };
}

function evaluateCompliance(c: Contractor, ctx: EligibilityContext): EligibilityFactor {
  if (hasDbBackedComplianceEvidence(c, ctx)) {
    return evaluateDbBackedCompliance(c, ctx);
  }
  return evaluateLegacyCompliance(c);
}

function evaluateCoverage(c: Contractor, ctx: EligibilityContext): EligibilityFactor {
  if (!ctx.job) {
    const has = c.serviceZipCodes.length > 0 || c.serviceRegions.length > 0;
    return {
      key: "coverage",
      label: "Coverage area",
      passed: has,
      blocking: true,
      reason: has ? "Coverage area defined." : "No coverage area defined.",
    };
  }
  const zip = ctx.job.address?.zip ?? "";
  const city = (ctx.job.address?.city ?? "").toLowerCase();
  const zipMatch = zip ? c.serviceZipCodes.includes(zip) : false;
  const regionMatch = c.serviceRegions.some(
    (r) => r.toLowerCase().includes(city) || (city && city.includes(r.toLowerCase())),
  );
  const passed = zipMatch || regionMatch;
  return {
    key: "coverage",
    label: "Coverage area",
    passed,
    blocking: true,
    reason: passed
      ? zipMatch
        ? `Zip ${zip} covered.`
        : `Region match for ${ctx.job.address?.city ?? "job city"}.`
      : `No coverage match for ${ctx.job.address?.city ?? "job"}${zip ? ` (${zip})` : ""}.`,
  };
}

function evaluateOptIn(c: Contractor): EligibilityFactor {
  const passed = c.marketplaceOptIn === true;
  return {
    key: "opt_in",
    label: "Marketplace opt-in",
    passed,
    blocking: true,
    reason: passed
      ? "Contractor has opted into marketplace."
      : "Contractor has not opted into the marketplace.",
  };
}

function evaluateScorecard(c: Contractor): EligibilityFactor {
  // Soft signal — never blocks. Threshold can be tuned later.
  const rating = Number(c.rating);
  const hasScorecardHistory = Number.isFinite(rating) && rating > 0;
  if (!hasScorecardHistory) {
    return {
      key: "scorecard",
      label: "Scorecard",
      passed: true,
      blocking: false,
      reason: "No completed job history yet.",
    };
  }
  const passed = rating >= 3.0;
  return {
    key: "scorecard",
    label: "Scorecard",
    passed,
    blocking: false,
    reason: passed
      ? `Rating ${rating.toFixed(1)}.`
      : `Below-threshold rating ${rating.toFixed(1)}.`,
  };
}

function evaluateOverride(c: Contractor): EligibilityFactor | null {
  const ov = c.marketplaceAdminOverride;
  if (!ov) return null;
  return {
    key: "override",
    label: `Admin override (${ov.decision})`,
    passed: ov.decision === "allow",
    blocking: false, // override is informational; resolveEligible applies its effect
    reason: `${ov.reason} — by ${ov.setBy} on ${ov.setAt.slice(0, 10)}`,
  };
}

export function resolveContractorEligibility(
  contractor: Contractor,
  ctx: EligibilityContext,
): ContractorEligibility {
  const factors: EligibilityFactor[] = [
    evaluateProfile(contractor),
    evaluatePayProfile(contractor, ctx),
    evaluateCompliance(contractor, ctx),
    evaluateCoverage(contractor, ctx),
    evaluateOptIn(contractor),
    evaluateScorecard(contractor),
  ];
  const override = evaluateOverride(contractor);
  if (override) factors.push(override);

  const blockingFail = factors.some((f) => f.blocking && !f.passed);
  const allBlockingPass = !blockingFail;

  let eligible = allBlockingPass;
  let overrideActive = false;
  if (contractor.marketplaceAdminOverride) {
    if (contractor.marketplaceAdminOverride.decision === "deny") {
      // Hard deny always wins regardless of factor results
      overrideActive = true;
      eligible = false;
    } else if (!allBlockingPass) {
      // Allow override only applies when there are still failing blocking factors
      overrideActive = true;
      eligible = true;
    }
    // If decision === "allow" but all blocking factors already pass,
    // the override is redundant — contractor is simply eligible, no override needed
  }

  return {
    contractorId: contractor.id,
    eligible,
    overrideActive,
    factors,
    computedAt: new Date().toISOString(),
  };
}

export function resolveEligibleContractorsForJob(
  job: Job,
  contractors: Contractor[],
  payProfiles: ContractorPayProfile[],
): ContractorEligibility[] {
  return contractors.map((c) =>
    resolveContractorEligibility(c, { payProfiles, job }),
  );
}

// ── Action-level guard ──
// Used by every action that creates a marketplace offer. UI gating is not
// sufficient — every offer-creation path MUST call this.

export class EligibilityAssertionError extends Error {
  readonly eligibility: ContractorEligibility;
  constructor(message: string, eligibility: ContractorEligibility) {
    super(message);
    this.name = "EligibilityAssertionError";
    this.eligibility = eligibility;
  }
}

export interface AssertEligibleResult {
  ok: boolean;
  eligibility: ContractorEligibility;
  failingFactors: EligibilityFactor[];
}

export function assertEligible(
  contractor: Contractor,
  ctx: EligibilityContext,
): AssertEligibleResult {
  const eligibility = resolveContractorEligibility(contractor, ctx);
  const failingFactors = eligibility.factors.filter(
    (f) => f.blocking && !f.passed,
  );
  // Override allows bypass even with failing blocking factors.
  if (eligibility.eligible) {
    return { ok: true, eligibility, failingFactors: [] };
  }
  return { ok: false, eligibility, failingFactors };
}
